<script setup lang="ts">
import { Button, Icon, Modal } from '@easybell/caramel-vue3'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { type Locale } from '@/utils/locale'

type MaintenanceModeModalMode =
  | 'maintenance'
  | 'service-unavailable'
  | 'resolved'
type MaintenanceModeMessageLocale = Exclude<Locale, 'debug'>
type MaintenanceModeMessageKey =
  | 'maintenance_in_progress'
  | 'maintenance_in_progress_description'
  | 'maintenance_completed'
  | 'maintenance_completed_description'
  | 'service_temporarily_unavailable'
  | 'service_temporarily_unavailable_description'

const localizedMessages = {
  en: {
    maintenance_in_progress: 'Maintenance in progress',
    maintenance_in_progress_description:
      'The Cloud PBX is currently unavailable while maintenance work is in progress. This dialog will update automatically when the service is available again.',
    maintenance_completed: 'Maintenance completed',
    maintenance_completed_description:
      'The Cloud PBX is available again. Reload the page to start fresh, or close this dialog to continue.',
    service_temporarily_unavailable: 'Service temporarily unavailable',
    service_temporarily_unavailable_description:
      'The Cloud PBX could not be reached. Please reload the page and try again later.',
  },
  de: {
    maintenance_in_progress: 'Wartungsarbeiten im Gange',
    maintenance_in_progress_description:
      'Die Cloud Telefonanlage ist derzeit nicht verfügbar, während Wartungsarbeiten durchgeführt werden. Dieser Dialog wird automatisch aktualisiert, sobald der Dienst wieder verfügbar ist.',
    maintenance_completed: 'Wartungsarbeiten abgeschlossen',
    maintenance_completed_description:
      'Die Cloud Telefonanlage ist wieder verfügbar. Laden Sie die Seite neu, um von vorne zu beginnen, oder schließen Sie diesen Dialog, um fortzufahren.',
    service_temporarily_unavailable: 'Dienst vorübergehend nicht verfügbar',
    service_temporarily_unavailable_description:
      'Die Cloud Telefonanlage konnte nicht erreicht werden. Bitte laden Sie die Seite neu und versuchen Sie es später erneut.',
  },
  fr: {
    maintenance_in_progress: 'Maintenance en cours',
    maintenance_in_progress_description:
      'La Téléphonie Cloud est actuellement indisponible pendant les travaux de maintenance. Cette boîte de dialogue se mettra automatiquement à jour lorsque le service sera de nouveau disponible.',
    maintenance_completed: 'Maintenance terminée',
    maintenance_completed_description:
      'La Téléphonie Cloud est de nouveau disponible. Rechargez la page pour repartir à zéro, ou fermez cette boîte de dialogue pour continuer.',
    service_temporarily_unavailable: 'Service temporairement indisponible',
    service_temporarily_unavailable_description:
      'La Téléphonie Cloud n’a pas pu être jointe. Veuillez recharger la page et réessayer plus tard.',
  },
  nl: {
    maintenance_in_progress: 'Onderhoud wordt uitgevoerd',
    maintenance_in_progress_description:
      'De Cloud PBX is momenteel niet beschikbaar terwijl er onderhoudswerkzaamheden worden uitgevoerd. Dit dialoogvenster wordt automatisch bijgewerkt zodra de dienst weer beschikbaar is.',
    maintenance_completed: 'Onderhoud voltooid',
    maintenance_completed_description:
      'De Cloud PBX is weer beschikbaar. Laad de pagina opnieuw om opnieuw te beginnen, of sluit dit dialoogvenster om door te gaan.',
    service_temporarily_unavailable: 'Dienst tijdelijk niet beschikbaar',
    service_temporarily_unavailable_description:
      'De Cloud PBX kon niet worden bereikt. Laad de pagina opnieuw en probeer het later nog eens.',
  },
} satisfies Record<
  MaintenanceModeMessageLocale,
  Record<MaintenanceModeMessageKey, string>
>

const messageKeysByMode: Record<
  MaintenanceModeModalMode,
  { title: MaintenanceModeMessageKey; description: MaintenanceModeMessageKey }
> = {
  maintenance: {
    title: 'maintenance_in_progress',
    description: 'maintenance_in_progress_description',
  },
  resolved: {
    title: 'maintenance_completed',
    description: 'maintenance_completed_description',
  },
  'service-unavailable': {
    title: 'service_temporarily_unavailable',
    description: 'service_temporarily_unavailable_description',
  },
}

const props = defineProps<{
  mode: MaintenanceModeModalMode
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'reload'): void
}>()

const i18n = useI18n()

const messageLocale = computed(() => {
  const locale = i18n.locale.value

  return locale in localizedMessages
    ? (locale as MaintenanceModeMessageLocale)
    : 'en'
})

const localizedMessage = computed(() => {
  const messageKeys = messageKeysByMode[props.mode]
  const modeMessages = localizedMessages[messageLocale.value]

  return {
    title: modeMessages[messageKeys.title],
    description: modeMessages[messageKeys.description],
  }
})
</script>

<template>
  <Modal size="sm" data-test="maintenance-mode-modal">
    <template #body>
      <div class="flex flex-col gap-5">
        <div
          class="w-12 h-12 rounded-full mx-auto flex items-center justify-center"
          :class="{
            'bg-warning-100': mode === 'maintenance',
            'bg-danger-100': mode === 'service-unavailable',
            'bg-success-100': mode === 'resolved',
          }"
        >
          <Icon
            v-if="mode === 'maintenance'"
            name="Spinner"
            class="animate-spin text-warning-600"
            size="1.5rem"
          />
          <Icon
            v-else-if="mode === 'resolved'"
            name="Checkmark"
            class="text-success-600"
            size="1.5rem"
          />
          <Icon v-else name="WarningInCircle" class="text-danger-600" size="1.5rem" />
        </div>

        <div class="flex flex-col gap-2">
          <div class="text-lg font-semibold text-center">
            {{ localizedMessage.title }}
          </div>

          <div class="text-sm text-center text-neutral-500">
            {{ localizedMessage.description }}
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <div
        v-if="mode === 'resolved'"
        class="flex flex-col sm:flex-row justify-center gap-2"
      >
        <Button
          size="sm"
          class="w-full"
          data-test="maintenance-reload-button"
          @click="emit('reload')"
        >
          {{ i18n.t('notifications.reload') }}
        </Button>
        <Button
          size="sm"
          color="neutral"
          class="w-full"
          data-test="maintenance-close-button"
          @click="emit('close')"
        >
          {{ i18n.t('global.close') }}
        </Button>
      </div>

      <div
        v-else-if="mode === 'service-unavailable'"
        class="flex justify-center"
      >
        <Button
          size="sm"
          class="w-full"
          data-test="maintenance-reload-button"
          @click="emit('reload')"
        >
          {{ i18n.t('notifications.reload') }}
        </Button>
      </div>
    </template>
  </Modal>
</template>
